import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { userScopeCleared } from '@krp/client-runtime'

import type { CommonCatalogIntentState } from './types'

/** Строк на странице: таблица читается без прокрутки на ноутбуке и не тянет лишнего. */
export const COMMON_CATALOG_PAGE_SIZE = 50
/** Как на сервере (`CatalogBrowse.MaxTextLength`): длиннее запрос не уходит. */
export const COMMON_CATALOG_MAX_QUERY = 128
/** Верхняя граница номера страницы при восстановлении вкладки: больший номер считается порченым и отбрасывается. */
const COMMON_CATALOG_MAX_PAGE = 10_000

const initialState: CommonCatalogIntentState = { query: '', page: 1 }

/**
 * Намерение человека в «Общем каталоге»: строка поиска и страница. Срок жизни —
 * `screen`; вкладка окна помнит их через `capture`/`restore`.
 */
const commonCatalogSlice = createSlice({
  name: 'platform/commonCatalog',
  initialState,
  reducers: {
    /** Новый запрос — с первой страницы: прежний номер к другой выдаче не относится. */
    catalogSearchChanged(state, action: PayloadAction<string>) {
      state.query = action.payload.slice(0, COMMON_CATALOG_MAX_QUERY)
      state.page = 1
    },
    catalogPageChosen(state, action: PayloadAction<number>) {
      state.page = Math.max(1, Math.trunc(action.payload))
    },
    catalogIntentRestored(_state, action: PayloadAction<CommonCatalogIntentState>) {
      return action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userScopeCleared, () => initialState)
  },
})

export const { catalogSearchChanged, catalogPageChosen, catalogIntentRestored } = commonCatalogSlice.actions
export const commonCatalogReducer = commonCatalogSlice.reducer

/** Снимок вкладки обратно в намерение; чужая форма — `undefined`, а не падение. */
export function decodeCommonCatalogIntent(value: unknown): CommonCatalogIntentState | undefined {
  if (typeof value !== 'object' || value === null) return undefined
  const { query, page } = value as Record<string, unknown>
  if (typeof query !== 'string' || query.length > COMMON_CATALOG_MAX_QUERY) return undefined
  if (typeof page !== 'number' || !Number.isInteger(page) || page < 1 || page > COMMON_CATALOG_MAX_PAGE) return undefined
  return { query, page }
}
